<template>
  <PreviewDocumentModal
    :open="modal_config.preview_open"
    @close-modal="modal_config.preview_open = false"
    :modal_config="modal_config"
  />
  <ShortTermOptOutModal
    :open="short_term_opt_out_open"
    @close-modal="short_term_opt_out_open = false"
    @refresh="handleOptOutRefresh"
    :investment="investmentDetails"
  />
  <ChangeBankingModal
    :open="modal_config.distribution_bank_open"
    @close-modal="modal_config.distribution_bank_open = false"
    @refresh="dataStore.setData(['investments'])"
    :modal_config="modal_config"
    :investment="investmentDetails"
    :current_distribution_method="investmentDetails?.distribution_method"
    :current_distribution_account="investmentDetails?.distribution_account"
  />
  <DisclaimerModal
    :open="isSignPendingS1DisclaimerModalOpen"
    @close-modal="isSignPendingS1DisclaimerModalOpen = false"
    @confirm="handleGeneratePendingS1SigningLink"
    disclaimer="By proceeding, you are finalizing your investment. Once signed, this action cannot be undone and no changes can be made to your investment details. Please ensure all investment details are correct before continuing."
    disclaimer_header="Registered Offering Quiet Period"
    title="Finalize Your Investment"
    subheader="Signing"
    cta_text="Confirm and Sign"
  />
  <SignInvestmentLoader
    v-if="UIStore.is_loading"
    ref="player"
    :key="UIStore.is_dark_mode"
    :animation-data="animationData"
  />
  <!-- Mobile: bottom CTA card -->
  <div
    class="flex md:hidden rounded-t-2xl justify-center fixed bottom-0 left-0 w-full z-50 bg-gradient-to-b"
    :class="UIStore.is_dark_mode ? 'from-primary-7 to-primary-6' : ' from-paper-5 to-paper-3'"
  >
    <div class="flex md:hidden w-full p-3">
      <div
        v-if="
          !dataStore.s1_offerings_in_quiet_period &&
          investmentDetails?.status === 'Pending S1' &&
          !isRetirementAccountType(investmentDetails?.type)
        "
        class="w-full"
      >
        <Button
          @click="isSignPendingS1DisclaimerModalOpen = true"
          variant="primary-grey"
          size="lg"
          class="w-full"
        >
          <template #label> Sign Investment </template>
        </Button>
      </div>
      <div v-else-if="isShowRolloverInvestment" class="w-full">
        <Button
          @click="handleRolloverInvestmentClick"
          variant="primary-grey"
          size="lg"
          class="w-full"
        >
          <template #label>
            <div class="font-medium font-inter">
              Reinvest ${{ commaSeparateThousands(adjustedRolloverAvailable) }}
            </div>
          </template>
        </Button>
      </div>
      <!-- <div v-else-if="isShowChangeInvestment" class="w-full">
        <Button
          @click="handleChangeInvestmentClick"
          variant="primary-grey"
          size="lg"
          class="w-full"
        >
          <template #label> Change Investment </template>
        </Button>
      </div> -->
    </div>
  </div>
  <div class="w-full flex items-center justify-center" v-if="is_loading">
    <LoadingSpinner
      :show="is_loading"
      :message="'Loading...'"
      :hide-overlay="true"
      position="fixed"
    />
  </div>

  <div v-else-if="investmentDetails" class="w-full mx-auto flex flex-col gap-4">
    <BreadCrumbs :crumbs="UIStore.breadCrumbs" />
    <Header overtitle="Investment Details" :headerNuggets="investmentNuggets">
      <template #title>
        <div class="flex flex-col md:flex-row gap-1.5 md:gap-3 md:items-center items-start w-full">
          <div
            class="hidden md:flex text-2xl md:text-3xl text-strongest font-semibold truncate text-nowrap"
          >
            {{ getInvestmentTitle() }}
          </div>
          <div
            class="flex md:hidden text-2xl md:text-3xl text-strongest font-semibold truncate text-nowrap"
          >
            {{ shortenInvestmentTitle(getInvestmentTitle()) }}
          </div>
          <div class="flex gap-3 items-center">
            <InfoPill
              v-for="pill in info_pills"
              :key="pill.message"
              :variant="pill.variant"
              :message="pill.message"
            />
          </div>
        </div>
      </template>
      <template #icon>
        <div
          class="flex items-center justify-center rounded-full bg-cta-primary-bg min-h-14 min-w-14 h-14 w-14"
        >
          <img :src="getInvestmentIcon(investmentDetails.offering_type)" class="w-full h-full" />
        </div>
      </template>
      <template #ctas>
        <Button
          v-if="
            !dataStore.s1_offerings_in_quiet_period &&
            investmentDetails.status === 'Pending S1' &&
            !isRetirementAccountType(investmentDetails.type)
          "
          @click="isSignPendingS1DisclaimerModalOpen = true"
          variant="primary-grey"
          size="lg"
          class="mr-2 hidden md:flex"
        >
          <template #label> Sign Investment </template>
        </Button>
        <Button
          v-else-if="isShowRolloverInvestment"
          @click="handleRolloverInvestmentClick"
          variant="primary-grey"
          size="lg"
          class="hidden md:flex"
        >
          <template #label>
            <div class="font-medium font-inter">
              Reinvest ${{ commaSeparateThousands(adjustedRolloverAvailable) }}
            </div>
          </template>
        </Button>
        <!-- <Button
          v-else-if="isShowChangeInvestment"
          @click="handleChangeInvestmentClick"
          variant="primary-grey"
          size="lg"
          class="hidden md:flex"
        >
          <template #label> Change Investment </template>
        </Button> -->
      </template>
      <template #type>
        <span class="text-2xl font-medium text-strongest text-green-700">
          {{ investmentDetails.type }}
        </span>
      </template>
    </Header>
    <DetailBanner
      v-if="dataStore.s1_offerings_in_quiet_period && investmentDetails.status === 'Pending S1'"
      variant="secondary"
    >
      <template #body>
        <AsteriskNotice text="Registered Offering Quiet Period" />
        <p class="font-inter font-medium text-strongest">
          Your signature will be collected once the quiet period ends. We'll notify you when it's
          time to finalize your investment.
        </p>
      </template>
    </DetailBanner>
    <DetailBanner
      v-if="
        dataStore.s1_offerings_in_quiet_period &&
        investmentDetails.status === 'Pending S1' &&
        investmentDetails.pending_s1_funded
      "
      variant="secondary"
    >
      <template #body>
        <AsteriskNotice text="Your Investment is Funded" />
        <p class="font-inter font-medium text-strongest">
          We have received funds for your investment.
        </p>
      </template>
    </DetailBanner>
    <DetailBanner v-if="showOptOutDecisionRegistered" variant="secondary">
      <template #body>
        <div
          class="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between w-full"
        >
          <div>
            <AsteriskNotice
              :text="
                investmentDetails.opt_out_auto_extend
                  ? 'Continue with Phoenix Flex?'
                  : 'Continuing with Phoenix Flex'
              "
              class="mb-2 md:mb-0"
            />
            <p class="font-inter text-strongest">
              {{
                investmentDetails.opt_out_auto_extend
                  ? "You elected to redeem some or all of your Phoenix Flex Notes. You can still opt to continue holding while the window remains open."
                  : "Congrats! You elected to continue holding your Phoenix Flex Notes. You may still request redemption while the window remains open."
              }}
              This window will close on {{ formatDate(noticeWindowCloseDate) }}.
            </p>
          </div>
          <Button variant="paper-primary" size="sm" @click="short_term_opt_out_open = true">
            <template #label>Change Decision</template>
          </Button>
        </div>
      </template>
    </DetailBanner>
    <DetailBanner
      v-if="!showOptOutDecisionRegistered && investmentDetails.is_short_term && noticeWindowMessage"
      variant="secondary"
    >
      <template #body>
        <div
          class="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between w-full"
        >
          <div>
            <AsteriskNotice
              text="
                Phoenix Flex Window
              "
              class="mb-2 md:mb-0"
            />
            <p class="font-inter text-strongest">
              {{ noticeWindowMessage }}
            </p>
          </div>
        </div>
      </template>
    </DetailBanner>
    <ChartContainer class="mb-2" :metrics="metrics" :type="'investment-details'">
      <template v-slot:chart-header>
        <div class="flex justify-between items-center">
          <div
            v-if="investmentStore.hasNoDistributions"
            class="text-xl md:mt-12 mt-5 md:text-3xl font-medium font-stretch-normal not-italic leading-none tracking-[-0.64] text-left text-strongest mb-1"
          >
            <IconLineChart
              class="w-[28.496px] h-[28.496px] mb-6 border-2.4 stroke-cta-tertiary-text"
            />
            Once your investment is processed, your returns will show up here.
            <div
              class="text-sm md:text-lg font-medium mt-1 font-stretch-normal not-italic leading-none tracking-normal text-left text-strong"
            >
              We’ll notify you when your first return is <br class="block md:hidden" />completed.
            </div>
          </div>
          <span
            v-else-if="
              !investmentStore.hasNoDistributions && !investmentStore.hasNoCompletedDistributions
            "
            class="text-xl font-semibold text-strongest mb-6"
            >Returns for this investment are up
            <span class="text-success"
              >{{ commaSeparateThousands(investmentStore.percentageGrowth.toFixed(2)) }}%</span
            ></span
          >
          <span
            v-else-if="investmentStore.hasNoCompletedDistributions"
            class="text-xl font-semibold text-strongest mb-6"
            >This investment has no completed returns yet</span
          >
        </div>
      </template>
    </ChartContainer>
    <div class="flex text-xl text-strongest md:hidden font-semibold">Investment Detail</div>
    <div class="mb-40">
      <TabView
        vertical
        :tabs="detail_tabs"
        ref="tabview"
        @update:activeTab="
          (tab) =>
            investmentTabViewed(tab, {
              investment_id: investmentDetails?.record_id,
              investment_type: investmentDetails?.offering_type,
              type_tag: investmentDetails?.type,
              status: investmentDetails?.status,
            })
        "
      >
        <!-- TODO -->
        <template #requires-action>
          <PaperCard>
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">
                You have {{ todo_items.length }} action{{ todo_items.length !== 1 ? "s" : "" }} to
                complete
              </div>
            </template>
            <template #body>
              <div
                v-for="item in todo_items"
                :key="item.message"
                class="rounded-2xl bg-bg-canvas p-4 mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
              >
                <div class="flex flex-col">
                  <AsteriskNotice
                    :text="item.asterisk_text"
                    :variant="item.asterisk_variant"
                    v-if="item.asterisk_text"
                  />
                  <div v-if="item.message" class="text-lg text-strongest font-inter font-semibold">
                    {{ item.message }}
                  </div>
                  <div v-if="item.subtext" class="text-sm text-strong font-inter">
                    {{ item.subtext }}
                  </div>
                </div>
                <div class="flex mt-2 md:mt-0" v-if="item.cta">
                  <Button
                    variant="primary-grey"
                    size="md"
                    @click="
                      () => {
                        todoItemClicked(item.type);
                        item.cta.onClick();
                      }
                    "
                  >
                    <template #icon v-if="item.cta?.icon">
                      <Icon :name="item.cta.icon" class="w-4 h-4 mr-1" />
                    </template>
                    <template #label
                      ><div class="font-inter">{{ item.cta?.label || "View" }}</div></template
                    >
                  </Button>
                </div>
              </div>
            </template>
          </PaperCard>
        </template>
        <!-- Investment Info -->
        <template #investment-info>
          <div class="rounded-xl w-full flex items-center justify-center flex-col gap-4">
            <!-- Offering Details -->
            <PaperCard :is_collapsable="true" class="mb-5 w-full">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">Offering</div>
              </template>

              <template #body>
                <Form
                  :schema_array="offeringSchema"
                  :state_object="offeringModel"
                  :is_disabled="true"
                  variant="primary"
                />
              </template>
            </PaperCard>
            <!-- Investment Details -->
            <PaperCard :is_collapsable="true" class="mb-5 w-full">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">Details</div>
              </template>

              <template #body>
                <Form
                  :schema_array="investmentSchema"
                  :state_object="investmentDetails"
                  :is_disabled="true"
                  variant="primary"
                />
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Returns -->
        <template #returns>
          <PaperCard class="w-full">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Returns</div>
            </template>
            <template #body>
              <Table
                :table_data="earnings_table_data"
                :table_headers="earnings_table_headers"
                variant="primary"
                :has_actions="false"
                mobile_primary_field="amount"
                :fields_with_custom_styles="['amount']"
                :hide_headers="false"
                :hide_scrollbar="true"
                :paginated="true"
                :pagination_page_size="6"
                empty_icon="BadgeDollarSign"
                empty_primary_text="No returns have been recorded for this investment yet."
                empty_secondary_text=""
                :column_gap_px="85"
                default_sort_field="distribution_date"
                :default_sort_ascending="false"
              >
                <template v-slot:amount="entry">
                  <div class="text-success">
                    {{
                      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                        entry.row.amount
                      )
                    }}
                  </div>
                </template>
              </Table>
            </template>
          </PaperCard>
        </template>
        <!-- Banking Info -->
        <template #banking-info>
          <div>
            <PaperCard class="w-full mb-5">
              <template #header>
                <div class="flex flex-col gap-1">
                  <div class="text-xl font-semibold text-strongest font-league">
                    Distribution Account
                  </div>
                  <div class="text-sm font-inter text-stronger w-3/4">
                    We distribute interest on the 10th of each month via ACH. Select your preferred
                    method and account for deposit. To change the account, click Edit to add a new
                    one.
                  </div>
                </div>
              </template>
              <template #body>
                <BankCard
                  :bank="distribution_bank"
                  :transparent-background="true"
                  :change-bank-button="false"
                  :custom-cta="true"
                  :custom-cta-label="'Edit'"
                  @cta-click="opendistributionBankModal()"
                />
              </template>
            </PaperCard>
            <PaperCard class="w-full mb-5">
              <template #header>
                <div class="flex flex-col gap-1">
                  <div class="text-xl font-semibold text-strongest font-league">
                    Funding Account
                  </div>
                  <div class="text-sm font-inter text-stronger w-3/4">
                    The account below was used to fund this investment.
                  </div>
                </div>
              </template>
              <template #body>
                <BankCard
                  v-if="investmentDetails.payment_method !== 'Card'"
                  :bank="funding_bank"
                  :transparent-background="true"
                  :change-bank-button="false"
                />
                <div
                  v-if="investmentDetails.payment_method === 'Card' && funding_bank.account_number"
                  class="flex items-center justify-between px-4 py-3 rounded-xl border border-outline-light"
                >
                  <div class="flex flex-col gap-0.5">
                    <div class="text-sm font-medium text-strongest font-inter">
                      {{ funding_bank.account_type }} {{ funding_bank.financial_institution_name }}
                    </div>
                    <div class="text-sm text-strong font-inter tracking-widest">
                      **** {{ funding_bank.account_number }}
                    </div>
                    <div
                      v-if="card_pi_status === 'success'"
                      class="text-xs font-medium text-success mt-0.5"
                    >
                      Successfully Charged
                    </div>
                    <div
                      v-else-if="card_pi_status === 'expired'"
                      class="text-xs font-medium text-error mt-0.5"
                    >
                      Payment Method Expired
                    </div>
                    <div
                      v-else-if="card_pi_status === 'declined'"
                      class="text-xs font-medium text-error mt-0.5"
                    >
                      Payment Declined
                    </div>
                  </div>
                  <Icon name="CreditCard" class="w-6 h-6 text-outline-darker flex-shrink-0" />
                </div>
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Reinvestment -->
        <template #reinvestments>
          <div class="rounded-xl w-full flex items-center justify-center flex-col gap-4">
            <PaperCard class="w-full" v-if="rollover_parent_table_data.length">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  Reinvestment Parents
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="rollover_parent_table_data"
                  :table_headers="rollover_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('reinvestment', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
            <PaperCard class="w-full" v-else>
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  {{
                    rollover_child_table_data.length == 0
                      ? "Reinvestments"
                      : "Reinvestment Children"
                  }}
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="rollover_child_table_data"
                  :table_headers="rollover_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  empty_icon="RotateCw"
                  empty_primary_text="No related Reinvestments."
                  empty_secondary_text=""
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('reinvestment', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Conversions -->
        <template #conversions>
          <div class="rounded-xl w-full flex items-center justify-center flex-col gap-4">
            <PaperCard class="w-full" v-if="conversion_parent_table_data.length">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  Conversion Parents
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="conversion_parent_table_data"
                  :table_headers="conversion_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('conversion', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
            <PaperCard class="w-full" v-else>
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  {{
                    conversion_child_table_data.length == 0 ? "Conversions" : "Conversion Children"
                  }}
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="conversion_child_table_data"
                  :table_headers="conversion_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  empty_icon="ArrowLeftRight"
                  empty_primary_text="No related Conversions."
                  empty_secondary_text=""
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('conversion', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Exchange Notes -->
        <template #exchange-notes>
          <div class="rounded-xl w-full flex items-center justify-center flex-col gap-4">
            <PaperCard class="w-full" v-if="exchange_parent_table_data.length">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  Exchange Parent{{ exchange_parent_table_data.length > 1 ? "s" : "" }}
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="exchange_parent_table_data"
                  :table_headers="exchange_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="
                          openRelatedInvestment(
                            'exchange',
                            entry.row,
                            entry.row.parent_investment_id
                          )
                        "
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
            <PaperCard class="w-full" v-else>
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  {{
                    exchange_child_table_data.length === 0
                      ? "Exchanges"
                      : "Exchange Child" + (exchange_child_table_data.length > 1 ? "ren" : "")
                  }}
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="exchange_child_table_data"
                  :table_headers="exchange_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  empty_icon="SwitchCamera"
                  empty_primary_text="No related Exchange Investments."
                  empty_secondary_text=""
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="
                          openRelatedInvestment(
                            'exchange',
                            entry.row,
                            entry.row.child_investment_id
                          )
                        "
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Title transfers -->
        <template #title-transfers>
          <div class="rounded-xl w-full flex items-center justify-center flex-col gap-4">
            <PaperCard class="w-full" v-if="title_transfer_parent_table_data.length">
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  Title Transfer Parents
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="title_transfer_parent_table_data"
                  :table_headers="title_transfer_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('title_transfer', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
            <PaperCard class="w-full" v-else>
              <template #header>
                <div class="text-xl font-semibold text-strongest font-league">
                  {{
                    title_transfer_child_table_data.length === 0
                      ? "Title Transfers"
                      : "Title Transfer Children"
                  }}
                </div>
              </template>
              <template #body>
                <Table
                  :table_data="title_transfer_child_table_data"
                  :table_headers="title_transfer_table_headers"
                  variant="primary"
                  :has_actions="true"
                  mobile_primary_field="amount"
                  :fields_with_custom_styles="['amount']"
                  :hide_headers="false"
                  :hide_scrollbar="false"
                  :paginated="true"
                  :pagination_page_size="6"
                  empty_icon="Handshake"
                  empty_primary_text="No related title transfer Investments."
                  empty_secondary_text=""
                  :column_gap_px="6"
                >
                  <template v-slot:amount="entry">
                    <div class="text-success">
                      {{
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(entry.row.amount)
                      }}
                    </div>
                  </template>
                  <template v-slot:actions="entry">
                    <div class="flex gap-1">
                      <Button
                        variant="darkpaper-primary"
                        size="sm"
                        @click="openRelatedInvestment('title_transfer', entry.row)"
                      >
                        <template #label>Details</template>
                      </Button>
                    </div>
                  </template>
                </Table>
              </template>
            </PaperCard>
          </div>
        </template>
        <!-- Signed documents -->
        <template #signed-documents>
          <PaperCard class="w-full" v-if="documents_table_data.length">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">
                You have
                {{ documents_table_data.length ? documents_table_data.length : "no" }} signed
                document{{ documents_table_data.length == 1 ? "" : "s" }}
              </div>
            </template>
            <template #body>
              <!-- <Table
                :table_data="documents_table_data"
                :table_headers="documents_table_headers"
                variant="primary"
                :has_actions="true"
                mobile_primary_field="file_name"
                :fields_with_custom_styles="[]"
                :hide_headers="false"
                :hide_scrollbar="false"
                :paginated="true"
                :pagination_page_size="6"
              >
                <template v-slot:actions="entry">
                  <div class="flex gap-1">
                    <Button
                      variant="darkpaper-primary"
                      size="sm"
                      @click="
                        router.push(
                          `/investment-details?recordId=${entry.row.parent_investment_id}`
                        )
                      "
                    >
                      <template #label>Preview</template>
                    </Button>
                    <Button
                      variant="darkpaper-primary"
                      size="sm"
                      @click="
                        router.push(
                          `/investment-details?recordId=${entry.row.parent_investment_id}`
                        )
                      "
                    >
                      <template #label>Download</template>
                    </Button>
                  </div>
                </template>
              </Table> -->
              <div
                v-for="doc in documents_table_data"
                class="flex flex-col md:flex-row items-start md:items-center justify-between rounded-2xl p-4 mb-3 bg-table-bg-on-input"
              >
                <div class="flex flex-col">
                  <InfoPill
                    :message="
                      doc?.file_category ??
                      (doc?.type == 'Sub Agreements' ? 'Sub Agreement' : doc?.type)
                    "
                    variant="filter"
                    icon="FileText"
                    class="mb-4 w-max"
                  />
                  <div class="flex flex-col gap-1">
                    <div class="text-strongest font-medium text-sm">{{ doc.file_name }}</div>
                    <div class="text-strong text-xs">
                      Signed on {{ formatDate(doc.created_date) }}
                    </div>
                  </div>
                </div>
                <div class="flex gap-4 mt-4 md:mt-0">
                  <Button variant="darkpaper-primary" size="sm" @click="previewDocument(doc)">
                    <template #label>Preview</template>
                    <template #icon>
                      <Icon name="Eye" class="w-4 h-4 opacity-100 text-outline-darker" />
                    </template>
                  </Button>
                  <Button variant="darkpaper-primary" size="sm" @click="downloadDocument(doc)">
                    <template #label>Download</template>
                    <template #icon>
                      <Icon name="Download" class="w-4 h-4 opacity-100 text-outline-darker" />
                    </template>
                  </Button>
                </div>
              </div>
            </template>
          </PaperCard>
          <div v-else class="flex h-full items-center justify-center">
            <PaperCard class="w-full h-full flex items-center justify-center">
              <template #body>
                <div class="flex flex-col items-center justify-center py-4 w-full min-w-0 gap-3">
                  <Icon name="Signature" class="w-12 h-12 opacity-100 text-outline-darker" />
                  <div class="text-strongest text-base font-medium mb-4">
                    No related signed documents.
                  </div>
                </div>
              </template>
            </PaperCard>
          </div>
        </template>
      </TabView>
    </div>
  </div>
  <div
    class="w-full mx-auto flex flex-col gap-6 h-full items-center justify-center absolute top-0"
    v-else
  >
    <div class="w-20 h-20 relative flex items-center justify-center rounded-full bg-cta-primary-bg">
      <div
        class="absolute right-0 top-0 bg-outline-light rounded-full w-7 h-7 p-1 flex items-center justify-center"
      >
        <Icon name="SearchX" class="w-5 h-5 opacity-100 text-outline-error" />
      </div>

      <Icon name="DollarSign" class="w-12 h-12 opacity-100 text-outline-darker" />
    </div>

    <div class="text-3xl text-strongest">Investment not found</div>
    <div class="text-xl text-strong">
      The investment you are looking for does not exist or has been removed.
    </div>
    <Button variant="paper-primary" size="lg" @click="router.push('/portfolio')">
      <template #label>Back to All Investments</template>
    </Button>
  </div>
</template>

<script setup>
// script for investment details
import { useRouter, useRoute } from "vue-router";
import {
  Button,
  Icon,
  BreadCrumbs,
  Header,
  TabView,
  ChartContainer,
  PaperCard,
  Form,
  InfoPill,
  Table,
  DetailBanner,
  AsteriskNotice,
  LoadingSpinner,
  SignInvestmentLoader,
  BankCard,
  DisclaimerModal,
} from "@/components";
import {
  useDataStore,
  useUIStore,
  useSchemaStore,
  useInvestmentStore,
  usePurchaseFlowStore,
  useCrudStore,
} from "@/stores";
import { onMounted, ref, computed, watch, onBeforeMount, nextTick } from "vue";
import { bankIsExpired, formatMonthYear } from "@/utilities/index.js";
import InvestmentIcon_Silver from "@/assets/images/investment_silver_circle.png";
import InvestmentIcon_Gold from "@/assets/images/investment_gold_circle.png";
import InvestmentIcon_GoldStar from "@/assets/images/investment_gold_star.png";
import InvestmentIcon_BlueArrows from "@/assets/images/investment_blue_arrows.png";
import InvestmentIcon_BlueTriangle from "@/assets/images/investment_blue_triangle.png";
import { IconLineChart } from "@/components/icons";

import { get } from "lodash";
import {
  commaSeparateThousands,
  formatID,
  formatDate,
  formatTerm,
  addUTCDays,
  isRetirementAccountType,
} from "@/utilities";
import PreviewDocumentModal from "../InvestorDocuments/PreviewDocumentModal.vue";
import ChangeBankingModal from "./ChangeBankingModal.vue";
import useFileUtility from "@/composables/useFileUtility";
import { usePlaidLink } from "@/composables/usePlaidLink";
import { useAnalytics } from "@/composables/useAnalytics";
import preparingDocLight from "@/assets/animations/preparing-document-light.json";
import preparingDocDark from "@/assets/animations/preparing-document-dark.json";
import ShortTermOptOutModal from "./ShortTermOptOutModal.vue";

const { downloadFile_R2, fetchFileForPreview_R2 } = useFileUtility();
const {
  investmentTabViewed,
  reinvestClicked,
  relatedInvestmentOpened,
  todoItemClicked,
  documentPreviewed,
  documentDownloaded,
  bankingInfoEditClicked,
} = useAnalytics();

const router = useRouter();
const route = useRoute();
const UIStore = useUIStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();
const investmentStore = useInvestmentStore();
const purchaseFlowStore = usePurchaseFlowStore();
const crudStore = useCrudStore();

const plaidLink = usePlaidLink();

const is_loading = ref(false);

let metrics = ref([]);
const isSignPendingS1DisclaimerModalOpen = ref(false);
const tabview = ref(null); // tabview component reference

let modals_open = {
  preview_open: false,
  distribution_bank_open: false,
};

const modal_config = ref({
  ...modals_open,
});
const todo_items = ref([]);
const short_term_opt_out_open = ref(false);

const showOptOutDecisionRegistered = computed(() => {
  const inv = investmentDetails.value;
  if (!inv?.is_short_term || !inv?.opt_out_decision_complete || !inv?.maturity_date) return false;
  const maturityDate = new Date(inv.maturity_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntilMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
  return daysUntilMaturity >= 30 && daysUntilMaturity <= 45;
});

// The Notice Window closes 30 days before maturity for short term investments
const noticeWindowCloseDate = computed(() => {
  const maturity_date = investmentDetails.value?.maturity_date;
  if (!maturity_date) return null;
  return addUTCDays(maturity_date, -30);
});

const noticeWindowStatus = computed(() => {
  if (!investmentDetails.value?.is_short_term) return null;
  const maturity_date = investmentDetails.value?.maturity_date;
  if (!maturity_date) return null;
  const maturityDate = new Date(maturity_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntilMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
  if (daysUntilMaturity > 45) return "upcoming";
  if (daysUntilMaturity >= 30) return "open";
  return "closed";
});

const noticeWindowMessage = computed(() => {
  const status = noticeWindowStatus.value;
  const maturity_date = investmentDetails.value?.maturity_date;
  if (!status || !maturity_date) return false;

  if (status === "open") return false;

  if (status === "upcoming") {
    const openDate = formatDate(addUTCDays(maturity_date, -45));
    const closeDate = formatDate(addUTCDays(maturity_date, -30));
    return `The next put window for this investment will be open from ${openDate} through ${closeDate}.`;
  }

  const next_put_date = investmentDetails.value?.next_put_date;
  if (!next_put_date) {
    return "The put window for this investment is closed. The next available put window is not yet available.";
  }
  const nextOpenDate = formatDate(addUTCDays(next_put_date, -45));
  const nextCloseDate = formatDate(addUTCDays(next_put_date, -30));
  return `The put window for this investment is closed. The next available put window will be open from ${nextOpenDate} through ${nextCloseDate}.`;
});

const detail_tabs = computed(() => {
  const default_tabs = [
    { label: "Investment Info", icon: "ChartLine" },
    { label: "Returns", icon: "BadgeDollarSign" },
    { label: "Banking Info", icon: "Landmark" },
    { label: "Reinvestments", icon: "RotateCw" },
    { label: "Conversions", icon: "ArrowLeftRight" },
    { label: "Title Transfers", icon: "HeartHandshake" },
    { label: "Signed Documents", icon: "Signature" },
  ];

  let tabs = [...default_tabs];

  let exchange_possible =
    investmentDetails.value.offering_type == "Reg A" ||
    investmentDetails.value.offering_type == "Exchange Note";

  if (exchange_possible) {
    tabs.splice(5, 0, { label: "Exchange Notes", icon: "SwitchCamera" });
  }

  if (todo_items.value.length) {
    tabs.unshift({
      label: "Requires Action",
      icon: "ListChecks",
      notif_badge_number: todo_items.value.length,
    });
  }

  return tabs;
});

onMounted(async () => {
  window.scrollTo(0, 0);
  plaidLink.initialize();
  todo_items.value = await getTodoItems();
  if (todo_items.value.length) {
    await nextTick();
    tabview.value?.onSelectTab("requires-action");
  }
});

function getInvestmentIcon(offering_type) {
  switch (offering_type) {
    case "Exchange Note":
      return InvestmentIcon_BlueArrows;
    case "Reg D":
      return InvestmentIcon_GoldStar;
    case "Reg A":
      return InvestmentIcon_BlueTriangle;
    case "S1":
      return InvestmentIcon_Gold;
    default:
      return InvestmentIcon_Silver;
  }
}

//// INVESTMENT DETAIL PROPERTIES ////

const investmentDetails = computed(() =>
  dataStore.investments
    ? dataStore.investments?.find((inv) => inv.record_id == route.query.recordId)
    : null
);

const investmentNuggets = computed(() => {
  if (!investmentDetails.value) return [];

  return [
    {
      label: "Amount",
      field_name: "amount",
      field_type: "number",
      number_type: "currency",
      v_model: investmentDetails.value.amount,
    },
    {
      label: "Bonds",
      field_name: "bonds",
      field_type: "number",
      v_model: investmentDetails.value.bonds,
    },
    {
      label: "Interest Rate",
      field_name: "interest_rate",
      field_type: "number",
      number_type: "percentage",
      v_model: investmentDetails.value?.interest_rate,
    },
    {
      label: "Status",
      field_name: "status",
      field_type: "string",
      v_model: investmentDetails.value?.status,
    },
    {
      label: "Maturity Date",
      field_name: "maturity_date",
      field_type: "date",
      v_model: investmentDetails.value?.maturity_date,
    },
    {
      label: "ID",
      field_name: "record_id",
      field_type: "string",
      v_model: investmentDetails.value?.record_id,
      truncate: true,
      is_object_id: true,
    },
  ];
});

const investmentSchema = computed(() => {
  return schemaStore.all_investment_schema
    .filter((item) => item.record_detail_config?.default?.record_detail_group === "Details")
    .sort(sortByDefaultGroupOrder)
    .map((item) => {
      return {
        ...item,
        field_type: item.field_type === "boolean" ? "yn" : item.field_type, // convert booleans to yes/no for better readability
      };
    });
});

// const offeringSchema = computed(() => {
//   return schemaStore.all_offering_schema
//     .filter((item) => item.record_detail_config?.default?.record_detail_group === "Details")
//     .sort(sortByDefaultGroupOrder);
// });

const offeringSchema = ref([
  {
    field_name: "offering_type",
    label: "Offering Type",
    field_type: "string",
  },
  {
    field_name: "interest_rate",
    label: "Interest Rate",
    field_type: "number",
    number_type: "percentage",
  },
  {
    field_name: "bond_term",
    label: "Term",
    field_type: "string",
  },
]);

const offeringModel = computed(() => {
  return {
    offering_type: investmentDetails.value?.offering_type,
    interest_rate: investmentDetails.value?.interest_rate,
    bond_term: !investmentDetails.value?.is_short_term
      ? formatTerm(investmentDetails.value?.bond_term)
      : `${investmentDetails.value?.bond_term} month term`,
  };
});

//// EARNINGS/RETURNS TABLE PROPERTIES ////

const earnings_table_data = computed(() => {
  // Filter distributions for this investment and adjust amount
  const filtered = dataStore.distributions
    ? dataStore.distributions
        .filter((earning) => earning.investment_id === investmentDetails.value.record_id)
        .map((earning) => {
          // clone the object to avoid mutating original data
          const adjusted = { ...earning };

          // cap the amount at the investment amount
          if (adjusted.amount > investmentDetails.value.amount) {
            adjusted.amount = adjusted.amount - investmentDetails.value.amount;
          }

          return adjusted;
        })
    : [];

  return filtered;
});

let earnings_table_headers = [
  { label: "Date", field_name: "distribution_date", field_type: "date" },
  { label: "Amount", field_name: "amount", field_type: "number", number_type: "currency" },
  { label: "Processed", field_name: "is_completed", field_type: "boolean" },
];

//// BANKING PROPERTIES ////

const distribution_bank = computed(() => {
  let empty_bank = {
    financial_institution_name: "",
    account_type: null,
    account_number: null,
    logo: null,
  };
  if (investmentDetails.value.distribution_method == "ACH") {
    if (!dataStore.bank_accounts || !investmentDetails.value) return empty_bank;
    let found_bank = dataStore.bank_accounts.find(
      (bank) => bank.record_id === investmentDetails.value.distribution_account
    );
    return (
      {
        ...found_bank,
        account_number: found_bank?.account_number,
        routing_number: found_bank?.routing_number,
      } ?? empty_bank
    );
  } else if (investmentDetails.value.distribution_method) {
    return {
      type: investmentDetails.value.distribution_method,
      financial_institution_name: "",
      account_type: null,
      account_number: null,
      logo: null,
    };
  } else {
    return empty_bank;
  }
});

const funding_bank = computed(() => {
  let empty_bank = {
    financial_institution_name: "",
    account_type: null,
    account_number: null,
    logo: null,
  };
  if (investmentDetails.value.payment_method == "ACH") {
    if (!dataStore.bank_accounts || !investmentDetails.value) return empty_bank;
    let found_bank = dataStore.bank_accounts.find(
      (bank) => bank.record_id === investmentDetails.value.funding_account
    );
    return (
      {
        ...found_bank,
        account_number: found_bank?.account_number,
        routing_number: found_bank?.routing_number,
      } ?? empty_bank
    );
  } else if (investmentDetails.value.payment_method == "Card") {
    const pi_id = investmentDetails.value.funding_account;
    const payment_intent = dataStore.payment_intents?.find((pi) => pi.pi_id === pi_id) ?? null;

    const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

    const fi = payment_intent
      ? `${capitalize(payment_intent.card_type)} ${capitalize(payment_intent.payment_method_type)}`.trim()
      : "";

    const acc_type = capitalize(payment_intent?.card_brand);

    return {
      type: investmentDetails.value.payment_method,
      financial_institution_name: fi,
      account_type: acc_type || null,
      account_number: payment_intent?.last4 ?? null,
      logo: null,
    };
  } else if (investmentDetails.value.payment_method) {
    return {
      type: investmentDetails.value.payment_method,
      financial_institution_name: "",
      account_type: null,
      account_number: null,
      logo: null,
    };
  } else {
    return empty_bank;
  }
});

// --- test flags ---
const force_expired = false;
const force_declined = false;
// ------------------

const card_pi_status = computed(() => {
  if (investmentDetails.value?.payment_method !== "Card") return null;

  const pi_id = investmentDetails.value.funding_account;
  const pi = dataStore.payment_intents?.find((p) => p.pi_id === pi_id) ?? null;

  if (force_expired) return "expired";
  if (force_declined) return "declined";
  if (!pi) return null;
  if (pi.status === "succeeded") return "success";
  if (pi.status === "canceled" && pi.cancellation_reason === "expired") return "expired";
  if (pi.status === "requires_payment_method") return "declined";
  return null;
});

const sortByDefaultGroupOrder = (a, b) =>
  get(a, "record_detail_config.default.record_detail_group_order", 0) -
  get(b, "record_detail_config.default.record_detail_group_order", 0);

//// ROLLOVER PROPERTIES ////

const rollover_table_headers = ref([
  { field_name: "signature_date", label: "Transaction Date", field_type: "date" },
  { field_name: "record_id", label: "Investment ID" },
  { field_name: "type", label: "Type" },
  { field_name: "status", label: "Status" },
  { field_name: "amount", label: "Amount", field_type: "number", number_type: "currency" },
  { field_name: "bonds", label: "Bonds", field_type: "number" },
  { field_name: "interest_rate", label: "Rate", field_type: "number", number_type: "percentage" },
  {
    field_name: "inception_to_date_interest_paid",
    label: "ITD Earned",
    field_type: "number",
    number_type: "currency",
  },
]);

const rollover_parent_table_data = computed(() => {
  const currentId = investmentDetails.value.record_id;

  // Standard path: use rollover_details
  const fromDetails = (dataStore.rollover_details || [])
    .filter((rollover) => rollover.child_id === currentId)
    .map((rollover) => {
      const investment = dataStore.investments?.find((inv) => inv.record_id === rollover.parent_id);
      return { ...investment };
    });

  // Fallback: if this investment is a pending unsigned child, find its parent
  const fromPending = (dataStore.investments || [])
    .filter((inv) => {
      return (
        investmentDetails.value.status === "Pending" &&
        !investmentDetails.value.signature_date &&
        investmentDetails.value.parent_investment === inv.record_id
      );
    })
    .map((inv) => ({ ...inv }));

  // Merge and dedupe by record_id
  const merged = [...fromDetails, ...fromPending];
  const seen = new Set();
  return merged.filter((inv) => {
    if (!inv.record_id || seen.has(inv.record_id)) return false;
    seen.add(inv.record_id);
    return true;
  });
});

const rollover_child_table_data = computed(() => {
  const currentId = investmentDetails.value.record_id;

  // Standard path: use rollover_details
  const fromDetails = (dataStore.rollover_details || [])
    .filter((rollover) => rollover.parent_id === currentId)
    .map((rollover) => {
      const investment = dataStore.investments?.find((inv) => inv.record_id === rollover.child_id);
      return { ...investment };
    });

  // Fallback: find pending unsigned children whose parent_investment is this one
  const fromPending = (dataStore.investments || [])
    .filter((inv) => {
      return inv.status === "Pending" && !inv.signature_date && inv.parent_investment === currentId;
    })
    .map((inv) => ({ ...inv }));

  // Merge and dedupe by record_id
  const merged = [...fromDetails, ...fromPending];
  const seen = new Set();
  return merged.filter((inv) => {
    if (!inv.record_id || seen.has(inv.record_id)) return false;
    seen.add(inv.record_id);
    return true;
  });
});

//// CONVERSION PROPERTIES ////

const conversion_table_headers = ref([
  { field_name: "signature_date", label: "Transaction Date", field_type: "date" },
  { field_name: "record_id", label: "Investment ID" },
  { field_name: "type", label: "Type" },
  { field_name: "status", label: "Status" },
  { field_name: "amount", label: "Amount", field_type: "number", number_type: "currency" },
  { field_name: "bonds", label: "Bonds", field_type: "number" },
  { field_name: "interest_rate", label: "Rate", field_type: "number", number_type: "percentage" },
  {
    field_name: "inception_to_date_interest_paid",
    label: "ITD Earned",
    field_type: "number",
    number_type: "currency",
  },
]);

const conversion_parent_table_data = computed(() => {
  let parent_ids = investmentDetails?.value?.conversion_parent_ids;
  if (!parent_ids || parent_ids.length === 0) return [];

  return dataStore.investments
    ? dataStore.investments.filter((inv) => parent_ids?.includes(inv.record_id))
    : [];
});

const conversion_child_table_data = computed(() => {
  let child_ids = investmentDetails?.value?.conversion_child_ids;
  if (!child_ids || child_ids.length === 0) return [];

  return dataStore.investments
    ? dataStore.investments.filter((inv) => child_ids?.includes(inv.record_id))
    : [];
});

//// EXCHANGE PROPERTIES ////

const exchange_table_headers = ref([
  { field_name: "exchange_date", label: "Exchange Date", field_type: "date" },
  { field_name: "type", label: "Type" },
  { field_name: "status", label: "Status" },
  { field_name: "amount", label: "Amount", field_type: "number", number_type: "currency" },
  { field_name: "record_id", label: "Investment ID" },
]);

// investments that are the parent in an exchange relationship (i.e. this investment is the child)
const exchange_parent_table_data = computed(() => {
  return dataStore.exchange_details
    ? dataStore.exchange_details.filter(
        (exchange) => exchange.child_investment_id === investmentDetails.value.record_id
      )
    : [];
});

// investments that are the child in an exchange relationship (i.e. this investment is the parent)
const exchange_child_table_data = computed(() => {
  return dataStore.exchange_details
    ? dataStore.exchange_details.filter(
        (exchange) => exchange.parent_investment_id === investmentDetails.value.record_id
      )
    : [];
});

const isShowChangeInvestment = computed(() => {
  const investment = investmentDetails.value;
  if (!investment) return false;

  const IRA_keywords = ["IRA", "401", "HSA"];
  return (
    investment.status === "Settled" &&
    !IRA_keywords.some((keyword) => investment.type.includes(keyword)) &&
    !dataStore.s1_offerings_in_quiet_period &&
    !investment.able_to_rollover &&
    !investment.is_pending_conversion &&
    !investment.is_title_transfer &&
    title_transfer_child_table_data.value.length === 0
  );
});

const isShowRolloverInvestment = computed(() => {
  const investment = investmentDetails.value;
  if (!investment?.able_to_rollover || investment.status !== "Settled") return false;
  return adjustedRolloverAvailable.value >= 1000;
});

const adjustedRolloverAvailable = computed(() => {
  const investment = investmentDetails.value;
  const pendingAmount = (dataStore.investments || [])
    .filter(
      (inv) =>
        inv.status === "Pending" &&
        !inv.signature_date &&
        inv.parent_investment === investment?.record_id
    )
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);

  return (investment?.rollover_available || 0) - pendingAmount;
});

//// TITLE TRANSFER PROPERTIES ////

const title_transfer_table_headers = ref([
  { field_name: "signature_date", label: "Transaction Date", field_type: "date" },
  { field_name: "record_id", label: "Investment ID" },
  { field_name: "type", label: "Type" },
  { field_name: "status", label: "Status" },
  { field_name: "amount", label: "Amount", field_type: "number", number_type: "currency" },
  { field_name: "bonds", label: "Bonds", field_type: "number" },
  { field_name: "interest_rate", label: "Rate", field_type: "number", number_type: "percentage" },
  {
    field_name: "inception_to_date_interest_paid",
    label: "ITD Earned",
    field_type: "number",
    number_type: "currency",
  },
]);

const title_transfer_parent_table_data = computed(() => {
  let parent_ids = investmentDetails?.value?.transfer_parent_ids;
  if (!parent_ids || parent_ids.length === 0) return [];
  return dataStore.investments
    ? dataStore.investments.filter((inv) => parent_ids?.includes(inv.record_id))
    : [];
});

const title_transfer_child_table_data = computed(() => {
  const currentInvestment = investmentDetails?.value;
  const childIds = currentInvestment.transfer_children_ids || [];

  if (childIds.length > 0) {
    return dataStore.investments.filter((inv) => childIds.includes(inv.record_id));
  }

  return dataStore.investments.filter((inv) =>
    inv.transfer_parent_ids?.includes(currentInvestment.record_id)
  );
});

//// DOCUMENTS PROPERTIES ////

const documents_table_headers = ref([
  { field_name: "type", label: "Type" },
  { field_name: "file_category", label: "Category" },
  { field_name: "file_name", label: "Name" },
  { field_name: "created_date", label: "Date", field_type: "date" },
  { field_name: "status", label: "Status" },
]);

const documents_table_data = computed(() => {
  var data = [];

  let sub = dataStore.sub_agreements
    ? dataStore.sub_agreements.filter(
        (sub) => sub.investment_id === investmentDetails.value.record_id
      )
    : [];

  if (sub) data.push(...sub);

  let crf = dataStore.documents.find(
    (doc) =>
      doc.file_category == "Conversion Request Form" &&
      doc?.investment_ids?.includes(investmentDetails.value.record_id)
  );

  if (crf) data.push(crf);

  return data;
});

/// ETC ///

const info_pills = computed(() => {
  let pills = [];
  if (exchange_parent_table_data.value.length > 0) {
    pills.push({ variant: "exchange", message: "Exchange Child" });
  }
  if (exchange_child_table_data.value.length > 0) {
    pills.push({ variant: "exchange", message: "Exchange Parent" });
  }
  if (rollover_parent_table_data.value.length > 0) {
    pills.push({ variant: "rollover", message: "Reinvestment Child" });
  }
  if (rollover_child_table_data.value.length > 0) {
    pills.push({ variant: "rollover", message: "Reinvestment Parent" });
  }
  if (conversion_parent_table_data.value.length > 0) {
    pills.push({ variant: "conversion", message: "Conversion Child" });
  }
  if (conversion_child_table_data.value.length > 0) {
    pills.push({ variant: "conversion", message: "Conversion Parent" });
  }
  if (title_transfer_parent_table_data.value.length > 0) {
    pills.push({ variant: "title-transfer", message: "Title Transfer Child" });
  }
  if (title_transfer_child_table_data.value.length > 0) {
    pills.push({ variant: "title-transfer", message: "Title Transfer Parent" });
  }
  if (
    investmentDetails.value.status === "Pending S1" &&
    !dataStore.s1_offerings_in_quiet_period &&
    !isRetirementAccountType(investmentDetails?.value?.type)
  ) {
    pills.push({ variant: "info", message: "Pending Signature" });
  }
  return pills;
});

onMounted(async () => {
  UIStore.is_chart_loading = true;
  window.scrollTo(0, 0);
  await getEarningsData();
  setBreadCrumbs();
});

async function getEarningsData() {
  setTimeout(() => {
    let chartData = investmentStore.getDashboardChartData(route.query.recordId);
    if (!investmentStore.hasNoDistributions) {
      const first = chartData[0];

      if (first) {
        const prevMonth = first.month === 1 ? 12 : first.month - 1;
        const prevYear = first.month === 1 ? first.year - 1 : first.year;

        chartData.unshift({
          year: prevYear,
          month: prevMonth,
          compoundedAmount: 0,
          runningNonCompoundedAmount: 0,
        });
      } else {
        chartData.unshift({
          year: first.year,
          month: first.month - 1,
          compoundedAmount: 0,
          runningNonCompoundedAmount: 0,
        });
      }
    }

    if (chartData.length) {
      investmentStore.chartData.labels = chartData.map((entry) =>
        formatMonthYear(entry.year, entry.month)
      );
      investmentStore.chartData.values = chartData.map(
        (entry) => entry.compoundedAmount + entry.runningNonCompoundedAmount
      );
      metrics.value = investmentStore.calculateDashboardInvestorMetrics(route.query.recordId);
      UIStore.is_chart_loading = false;
    }
    UIStore.is_chart_loading = false;
  });
}

// watch route changes to update breadcrumbs
watch(
  () => route.query.recordId,
  async (newRecordId, oldRecordId) => {
    window.scrollTo(0, 0);
    if (newRecordId !== oldRecordId) {
      setBreadCrumbs();
      if (tabview.value) {
        tabview.value.onSelectTab("investment-info");
      }
      todo_items.value = await getTodoItems();
    }
    // set chart loader for 1s to show loading state when switching between investments
    spinLoader();
  }
);

function setBreadCrumbs() {
  // console.log("setting breadcrumbs");
  let crumbs = UIStore.breadCrumbs;
  let new_crumb = {
    name: `${investmentDetails.value ? formatID(investmentDetails.value.record_id) : ""}`,
    path: `/investment-details?recordId=${investmentDetails.value ? investmentDetails.value.record_id : ""}`,
    is_id: true,
  };

  if (!crumbs.some((crumb) => crumb.name === "Portfolio")) {
    crumbs.push({
      name: "Portfolio",
      path: "/portfolio",
    });
  }

  // add the new crumb if it doesn't already exist
  if (!crumbs.some((crumb) => crumb.path === new_crumb.path)) {
    crumbs.push(new_crumb);
  }
  UIStore.breadCrumbs = crumbs;
}

// S1 quiet period utils + refs
async function handleGeneratePendingS1SigningLink() {
  const investment_id = investmentDetails.value.record_id;
  if (purchaseFlowStore.signing_link_invalidated || !purchaseFlowStore.signing_link) {
    UIStore.is_loading = true;
    await purchaseFlowStore.generateSigningLink(investment_id);
  }

  // Wait out the remaining 15s buffer
  const remaining = purchaseFlowStore.signingLinkTimeRemaining;
  if (remaining > 0) {
    UIStore.is_loading = true;
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }

  window.location.href = purchaseFlowStore.signing_link;
  UIStore.is_loading = false;
}
const animationData = computed(() => (UIStore.is_dark_mode ? preparingDocDark : preparingDocLight));

function handleChangeInvestmentClick() {
  router.push(`/change-investment?recordId=${investmentDetails.value.record_id}`);
}

function handleRolloverInvestmentClick() {
  reinvestClicked(investmentDetails.value.record_id, adjustedRolloverAvailable.value);
  router.push(`/make-an-investment?rolloverInvestment=${investmentDetails.value.record_id}`);
}

function openRelatedInvestment(relationship, row, navId = null) {
  relatedInvestmentOpened(relationship, investmentDetails.value.record_id, {
    investment_id: row.record_id ?? null,
    investment_type: row.offering_type ?? null,
    status: row.status ?? null,
    amount: row.amount ?? null,
    type_tag: row.type ?? null,
  });
  router.push(`/investment-details?recordId=${navId ?? row.record_id}`);
}

// END S1 quiet period utils

function getInvestmentTitle() {
  if (!investmentDetails.value) return "Investment Details";
  // for S1, use "Registered Offering (S1)"
  if (investmentDetails.value.offering_type === "S1") {
    return "Registered Offering (S1) Investment";
  }

  let title = `${investmentDetails?.value.offering_type || ""} Investment`;
  return title;
}

function shortenInvestmentTitle(title) {
  let short = title.replace("Investment", "").replace("Registered", "Reg.").trim();
  return short;
}

async function previewDocument(doc) {
  documentPreviewed(doc.file_category ?? doc.type ?? null);
  let file_url = await fetchFileForPreview_R2(doc);

  modal_config.value = {
    ...modals_open,
    preview_open: true,
    record: doc,
    file_url: file_url,
  };
}

async function opendistributionBankModal() {
  bankingInfoEditClicked("distribution");
  modal_config.value = {
    ...modals_open,
    distribution_bank_open: true,
  };
}

function downloadDocument(doc) {
  documentDownloaded(doc.file_category ?? doc.type ?? null);
  downloadFile_R2(doc);
}

async function handleOptOutRefresh() {
  todo_items.value = await getTodoItems();
  // and then change active tab to investment info
  if (tabview.value) {
    tabview.value.onSelectTab("investment-info");
  }
}

function spinLoader(time = 500) {
  is_loading.value = true;
  setTimeout(() => {
    is_loading.value = false;
  }, time);
}

async function getTodoItems() {
  // console.log("Getting todo items for investment details");

  let res = [];

  if (!investmentDetails.value) return res;

  // console.log(
  //   "Checking for todo items with investment details:",
  //   investmentDetails.value.distribution_method
  // );
  // console.log(
  //   "Distribution account details:",
  //   dataStore.bank_accounts.find(
  //     (bank) => bank.record_id === investmentDetails.value.distribution_account
  //   )
  // );
  // console.log(
  //   bankIsExpired(
  //     dataStore.bank_accounts.find(
  //       (bank) => bank.record_id === investmentDetails.value.distribution_account
  //     )
  //   )
  // );

  // check if this investment is a pending S1 investment that needs to be signed
  if (
    !dataStore.s1_offerings_in_quiet_period &&
    investmentDetails.value.offering_type === "S1" &&
    investmentDetails.value.status === "Pending S1" &&
    !isRetirementAccountType(investmentDetails.value.type)
  ) {
    res.push({
      type: "pending_s1_signature",
      asterisk_text: "Required Action",
      asterisk_variant: "error",
      message: "Signature needed: Pending Registered Offering (S1) Investment",
      subtext:
        "Please review and sign your pending Registered Offering (S1) investment to complete your purchase.",
      cta: {
        label: "Sign",
        onClick: () => (isSignPendingS1DisclaimerModalOpen.value = true),
      },
    });
  }

  // check for change investment children
  const changeInvestmentChildren = await crudStore.find("tempinvestments", [
    { field_name: "conversion_parent_ids", operator: "contains", value: route.query.recordId },
  ]);

  // console.log("Change investment children:", changeInvestmentChildren);

  // if (changeInvestmentChildren.length) {
  //   // console.log("Adding pending change investment todo item");
  //   res.push({
  //     type: "change_investment_children",
  //     asterisk_text: "Required Action",
  //     asterisk_variant: "error",
  //     message: "Pending Change Investment",
  //     subtext: "You have a pending change investment associated with this investment.",
  //     cta: {
  //       label: "View",
  //       onClick: () => {
  //         router.push(`/change-investment?recordId=${investmentDetails.value.record_id}`);
  //       },
  //     },
  //   });
  // }

  if (investmentDetails.value.distribution_method == "ACH") {
    let distribution_bank = dataStore.bank_accounts.find(
      (bank) => bank.record_id === investmentDetails.value.distribution_account
    );

    if (
      distribution_bank &&
      bankIsExpired(distribution_bank.created_date, distribution_bank.financial_institution_name)
    ) {
      res.push({
        type: "expired_bank",
        asterisk_text: "Required Action",
        asterisk_variant: "error",
        message: "Bank connection refresh needed: Distribution Account",
        subtext: "As part of our security checks, we need you to reconnect your PNC bank accounts.",
        cta: {
          label: "Reconnect",
          icon: "SquareArrowOutUpRight",
          onClick: () => {
            tabview.value.onSelectTab("banking-info");
          },
        },
      });
    }
  }

  // Do we need to make a TODO for funding accounts? Funds have already been pulled, allegedly.
  // if (investmentDetails.value.payment_method == "ACH") {
  //   let funding_bank = dataStore.bank_accounts.find(
  //     (bank) => bank.record_id === investmentDetails.value.funding_account
  //   );

  //   if (
  //     funding_bank &&
  //     bankIsExpired(funding_bank.created_date, funding_bank.financial_institution_name)
  //   ) {
  //     res.push({
  //       type: "expired_bank",
  //       asterisk_text: "Required Action",
  //       asterisk_variant: "error",
  //       message: "Bank connection refresh needed: Funding Account",
  //       subtext: "As part of our security checks, we need you to reconnect your PNC bank accounts.",
  //     });
  //   }
  // }

  // If we are on a short term investment within 45 days of maturity and decision not yet made
  if (investmentDetails.value.is_short_term && investmentDetails.value.maturity_date) {
    const maturityDate = new Date(investmentDetails.value.maturity_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));

    let sub_text = `Your Notice Window is open. Continue holding your Notes or request to redeem them. If no selection is made, your Notes will automatically continue. The Notice Window closes on ${formatDate(noticeWindowCloseDate.value)}.`;

    if (
      daysUntilMaturity >= 30 &&
      daysUntilMaturity <= 45 &&
      !investmentDetails.value.opt_out_decision_complete
    ) {
      res.push({
        type: "opt_out_decision",
        asterisk_text: "Required Action",
        asterisk_variant: "info",
        message: "Keep earning with Phoenix Flex",
        subtext: sub_text,
        cta: {
          label: "Make Selection",
          icon: "Clock",
          onClick: () => {
            short_term_opt_out_open.value = true;
          },
        },
      });
    }
  }

  return res;
}
</script>
